// Eskom PARCC — optimistic-concurrency guard.
//
// A write surface loads a transaction at some `rowVersion` X, the user edits,
// and then saves. Between load and save another writer may have advanced the
// row. `checkRowVersion` re-reads the CURRENT server rowVersion (via the read
// layer's resolveTransactionRef) and compares it to X: equal → safe to write;
// different → a conflict the caller should surface (refetch / merge / warn).
//
// IMPORTANT — CLIENT-SIDE DETECTION ONLY. The check and the subsequent write are
// two separate round-trips, and the current UpdateTransactionData mutation
// updates by id WITHOUT an atomic rowVersion compare-and-set (no `WHERE
// rowVersion = X` predicate, no server-side increment). So this guard NARROWS
// the last-write-wins window but does NOT eliminate it: a racing writer can still
// slip in between the check and the write. Eliminating the race entirely needs a
// connector change — an atomic CAS mutation (e.g. update … where id = $id and
// rowVersion = $expected, returning the new rowVersion) — which is out of scope
// for this task (no SDK regeneration / new connector ops) and is flagged for a
// later task.
import { resolveTransactionRef } from './db';

/**
 * Outcome of an optimistic-concurrency check. On conflict, `current` carries the
 * server's present rowVersion, or `null` when the reference no longer resolves
 * (the transaction was deleted or never existed) — either way the caller must
 * not blindly proceed with a stale write.
 */
export type RowVersionCheck = { ok: true } | { ok: false; current: number | null };

/**
 * Re-read the transaction for `referenceNo` and compare its current rowVersion
 * to `expectedRowVersion` (the value the client loaded). Returns `{ ok: true }`
 * when they match, otherwise `{ ok: false, current }` (current `null` if the
 * reference no longer resolves). See the module note: detection is client-side
 * only and does not provide an atomic guarantee.
 */
export async function checkRowVersion(
  referenceNo: string,
  expectedRowVersion: number,
): Promise<RowVersionCheck> {
  const tx = await resolveTransactionRef(referenceNo);
  if (!tx) return { ok: false, current: null };
  if (tx.rowVersion === expectedRowVersion) return { ok: true };
  return { ok: false, current: tx.rowVersion };
}
